import React, { useState, useEffect, useRef } from "react";
import styles from "./Style.module.css";
import { useGlobalContext } from "../../../Context/GlobalContext";
import { getSearchResults } from "../../../APICalling/APICalling";
import CustomModal from "../../../Components/Modal/CustomModal";
import MultiStatusSelect from "../../../Components/DropDown/MultiStatusSelect";
import DatePicker from "react-datepicker";
import { formatDateTime, getInitials, getConsistentColor, highlightQuery } from "../../../Utils/utils";
import { defaultStatus } from "../../../Utils/Constants/defaultStatus";
import IconChina from "../../../Assets/images/china.svg";
import IconThai from "../../../Assets/images/thai.svg";
import IconHard from "../../../Assets/images/hard.svg";
import IconSoft from "../../../Assets/images/soft.svg";
import IconRequested from "../../../Assets/images/requested.svg";
import SearchIcon from "../../../Components/Icons/SearchIcon"
import Placeholder from "../../../Components/Placeholder/Placeholder";
import CloseCircleIcon from "../../../Components/Icons/CloseCircleIcon";
import SearchNotFoundIcon from "../../../Components/Icons/SearchNotFoundIcon";
import CalendarIcon from "../../../Components/Icons/CalendarIcon";
import DropdownNoImage from "../../../Components/DropDown/DropdownNoImage";
import Loader from "../../../Components/Loader/Loader";
import logger from "../../../Utils/logger";

const Search = ({ closePopup, onBookingSelect }) => {
    const {
		selectedOutlet, 
		outletOptions,
		selectedOutletName,
	} = useGlobalContext();

	// Helper to format Date → "YYYY-MM-DD" in local time (no timezone shift)
	const formatToYYYYMMDD = (dateInput) => {
		if (!dateInput) return "";
		const d = new Date(dateInput);
		if (Number.isNaN(d.getTime())) return "";
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		return `${yyyy}-${mm}-${dd}`;
	};

	const [query, setQuery] = useState("");
	const [status, setStatus] = useState([]);
	// Do not prefill date by default; keep empty until user selects
	const [selectedSearchDate, setSelectedSearchDate] = useState("");
	const [results, setResults] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const resultsRef = useRef(null);
	const isLoadingRef = useRef(false);
	const requestIdRef = useRef(0);
	const [showCalendar, setShowCalendar] = useState(false);
	const [searchInitiated, setSearchInitiated] = useState(false);
	const [outlet, setOutlet] = useState(selectedOutlet);

    const clearInput = () => {
        setQuery("");
        searchInputRef.current?.focus();
    };

    const clearDate = () => {
        setSelectedSearchDate("");
        setShowCalendar(false);
    };

    const filterOption = [
		...defaultStatus.filter((status) => status !== "Holding"),
		// "Holding", // Hold booking filter disabled
		"Cancelled",
		"No-show",
	].map((status) => ({ text: status }));

	const searchInputRef = useRef(null);
	useEffect(() => {
		// Add a small delay to ensure modal is fully rendered before focusing
		const timer = setTimeout(() => {
			if (searchInputRef.current) {
				searchInputRef.current.focus();
			}
		}, 100);
		
		return () => clearTimeout(timer);
	}, []);


	const handleOutletSelect = (selectedOption) => {
		setOutlet(selectedOption?.value);
	};


    // GET SEARCH RESULT START
	const fetchSearchResults = async ({ pageNum = 1, append = false } = {}) => {
		const requestId = ++requestIdRef.current;
		isLoadingRef.current = true;
		setIsLoading(true);
		setSearchInitiated(true);
		// Ensure booking_date is always a YYYY-MM-DD string
		const bookingDateParam =
			typeof selectedSearchDate === "string"
				? selectedSearchDate
				: formatToYYYYMMDD(selectedSearchDate);

		// Map UI-selected statuses to API-expected values
		const mappedStatus = (status || []).map((s) => {
			const val = (s || "").trim();
			if (val === "No Show") return "No-show";
			if (val === "Chescked-in" || val === "Checked-in" || val === "Checked in") return "Check-in (In Progress)";
			return val;
		}).join(",");

		let item = {
			pagination: 1,
			page: pageNum,
			keyword: query,
			booking_date: bookingDateParam,
			status: mappedStatus,
			outlet: outlet,
		};
		try {
			const response = await getSearchResults(item);
			if (requestId !== requestIdRef.current) return;
			const data = response.data?.data?.data?.list || response.data?.data?.list || [];
			const pagination = response.data?.data?.data?.pagination || response.data?.data?.pagination;
			setResults((prev) => (append ? [...prev, ...data] : data));
			if (pagination) {
				setHasMore(pagination.currentPage < pagination.lastPage);
			} else {
				setHasMore(data.length > 0);
			}
		} catch (error) {
			if (requestId !== requestIdRef.current) return;
			console.error("Search failed:", error);
			if (!append) setResults([]);
			setHasMore(false);
		} finally {
			if (requestId === requestIdRef.current) {
				isLoadingRef.current = false;
				setIsLoading(false);
			}
		}
	};

    // GET SEARCH RESULT END

	useEffect(() => {
		// Prevent search on initial load if no filters are set
		if (query.trim() === "" && !selectedSearchDate && status.length === 0) {
			requestIdRef.current++;
			isLoadingRef.current = false;
			setIsLoading(false);
			setSearchInitiated(false);
			setResults([]);
			setPage(1);
			setHasMore(true);
			return;
		}

		const delayDebounce = setTimeout(() => {
			setPage(1);
			setHasMore(true);
			if (resultsRef.current) resultsRef.current.scrollTop = 0;
			fetchSearchResults({ pageNum: 1, append: false });
		}, 300);

		return () => clearTimeout(delayDebounce);
	}, [query, selectedSearchDate, status, outlet]);

	useEffect(() => {
		if (page === 1) return;
		fetchSearchResults({ pageNum: page, append: true });
	}, [page]);

	const handleResultsScroll = () => {
		const el = resultsRef.current;
		if (!el || isLoadingRef.current || !hasMore) return;
		const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
		if (nearBottom) {
			isLoadingRef.current = true;
			setPage((prev) => prev + 1);
		}
	};


	const getServiceTypeIcons = (serviceTypes = [], isRequested = false) => {
		const flattened = serviceTypes.flatMap((type) =>
			typeof type === "string"
				? type.split(",").map((t) => t.trim().toLowerCase())
				: []
		);

		const uniqueTypes = [...new Set(flattened)];

		const icons = [];

		if (uniqueTypes.includes("china"))
			icons.push({ icon: IconChina, title: "China" });
		if (uniqueTypes.includes("thai"))
			icons.push({ icon: IconThai, title: "Thai" });
		if (uniqueTypes.includes("soft"))
			icons.push({ icon: IconSoft, title: "Soft" });
		if (uniqueTypes.includes("hard"))
			icons.push({ icon: IconHard, title: "Hard" });

		if (isRequested)
			icons.push({ icon: IconRequested, title: "Requested Therapist" });

		return icons;
	};

	const getStatusClass = (status = "") => {
		return status.toLowerCase().replace(/\s+/g, "-");
	};

	// Custom read-only input component that prevents typing but displays selected date
	const ReadOnlyDateInput = React.forwardRef(({ value, onClick, placeholder, customOnClick }, ref) => {
		const handleClick = (e) => {
			e.preventDefault();
			// Use custom click handler if provided, otherwise use default onClick
			if (customOnClick) {
				customOnClick(e);
			} else if (onClick) {
				onClick(e);
			}
		};

		const handleKeyDown = (e) => {
			// Allow Tab for navigation, Enter and Space to open calendar
			if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ') {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					handleClick(e);
				}
				return;
			}
			// Prevent all other keyboard input
			e.preventDefault();
		};

		return (
			<input
				ref={ref}
				type="text"
				value={value || ''}
				onClick={handleClick}
				onChange={() => {}} // Prevent direct text changes
				onKeyDown={handleKeyDown}
				onPaste={(e) => e.preventDefault()}
				placeholder={placeholder}
				readOnly
				className={`${styles.customDatepicker} form-control rounded-0`}
				style={{ cursor: 'pointer' }}
			/>
		);
	});
	ReadOnlyDateInput.displayName = 'ReadOnlyDateInput';

	return (
		<>
			<CustomModal
				modalClass={`${styles.searchModalOverlay}`}
                customClassModal={`${styles.searchModal}`}
                customClassBody={`${styles.searchBody}`}
				show={true}
				onHide={closePopup}>
				<div className={styles.homeSearch}>
					<div className={styles.Header}>
						<div className={styles.outletDetails}><i>Outlet:</i>
							{selectedOutletName}
							{/* <DropdownNoImage options={outletOptions}
								selectedLabel={
								outletOptions.find(
								(outlet) => outlet.value === selectedOutlet
								)?.label || ""
								}
								onSelect={handleOutletSelect}
								dropdownClass={styles.outletFilter}
								placeholder="Outlet"
								placeholderClass="greyPlaceholder"
								labelText=""
							/> */}
						</div>

                        <div className={styles.searchBar}>
                            <div  className={styles.icon}>
                                <SearchIcon/>
                            </div>
                            <input
                                ref={searchInputRef}
                                type="search"
                                placeholder="Search by name or phone"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="form-control rounded-0"
                            />
                            {query && (
                                <div className={styles.clearIcon} onClick={clearInput}>
                                <CloseCircleIcon />
                                </div>
                            )}
                        </div>
						<div className={styles.filterCard}>
							<div className={styles.fc}>
								<MultiStatusSelect
									options={filterOption}
									placeholder="All"
									defaultSelected={status}
									onChange={(selectedStatusArray) =>
										setStatus(selectedStatusArray)
									}
									dropdownClass={`${styles.statusFilterClass}`}
                                    dropdownToggleClass={`${styles.dropdownToggleCustom} d-flex justify-between align-center`}
									labelText=""
								/>
							</div>
                            <div className={styles.fc}>
                                <div className={styles.pickerWrapper}>
									<DatePicker
										selected={
											selectedSearchDate ? new Date(selectedSearchDate) : null
										}
										onChange={(date) => {
											setSelectedSearchDate(formatToYYYYMMDD(date));
											setShowCalendar(false);
										}}
										onSelect={(date) => {
											setSelectedSearchDate(formatToYYYYMMDD(date));
											setShowCalendar(false);
										}}
										onClickOutside={() =>
											setShowCalendar(false)
										}
										open={showCalendar}
										dateFormat="EEE, MMM d"
										placeholderText="Select Date"
										customInput={
											<ReadOnlyDateInput 
												customOnClick={() => setShowCalendar(!showCalendar)} 
											/>
										}
									/>
									{selectedSearchDate ? (
										<div
											className={styles.clearDateIcon}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												clearDate();
											}}
											role="button"
											aria-label="Clear date"
										>
											<CloseCircleIcon />
										</div>
									) : (
										<div
											className={styles.calenderIcon}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setShowCalendar((prev) => !prev);
											}}
											role="button"
											aria-label="Open date picker"
										>
											<CalendarIcon size={20}/>
										</div>
									)}
								</div>
                            </div>
						</div>
					</div>
					<div
						className={styles.results}
						onScroll={handleResultsScroll}
						ref={resultsRef}
					>
						{isLoading && page === 1 ? (
							<div className={styles.initialLoader}>
								<Loader />
							</div>
						) : !searchInitiated ? (
							<div className={styles.noResults}>
								<Placeholder
                                    icon={<SearchIcon size={20} color="white"/>}
                                    heading="Search bookings by phone/name"
                                    para="Search for bookings by entering at least 3 characters of the phone number or name."
                                />
							</div>
						) : results.length > 0 ? (
							results.map((booking, index) => {
								const isRequested =
									booking.therapists &&
									!booking.therapists
										.toLowerCase()
										.includes("auto-assign");
								const serviceTypeIcons = getServiceTypeIcons(
									booking.service_type,
									isRequested
								);
								const formattedCreateDate = booking.booking_date
									? formatDateTime(booking.booking_date)
									: "-";

								return (
									<div
										key={index}
										className={styles.resultItem}
										onClick={() => {
											logger.log("this is called booking", booking);
											onBookingSelect({
												booking_id: booking.id,
												booking_date: booking.booking_date,
												booking,
											});
										}}
>
										<div className={styles.statusBar}>
											<div className={styles.cl}>
												<span className={`${styles.dot} ${getStatusClass(booking.booking_status)}`}
												/>
												<span className="commonFontClass">
													{booking.booking_status}
												</span>
											</div>
											<div className="commonFontClass">
												{formattedCreateDate}
											</div>
										</div>

										<div className={styles.details}>
											<div className={`${styles.customerDetails} d-flex justify-between`}>
												<div className={`${styles.customerResult} d-flex align-center`}>
													<div className={styles.avatar} style={{backgroundColor: getConsistentColor(booking.customer_id || booking.user_id, booking.customer)}}>
														<span>
															{getInitials(booking.customer)}
														</span>
													</div>
													<div className="commonBigFontClass">
														{highlightQuery(
															booking.customer,
															query
														)}
													</div>
												</div>
												<div
													className={`${styles.contactDetails} d-flex`}>
													<label className="greyFontClass">
														Phone:
													</label>
													<p className="greyFontClass">
														{highlightQuery(
															booking.customer_contact_number,
															query
														)}
													</p>
												</div>
											</div>
											<div
												className={`${styles.serviceDetails}  d-flex justify-between`}>
												<div>
													<label>Pax: </label>
													<p className="commonFontClass">
														{booking.total_pax}
													</p>
												</div>
												<div>
													<label>Services: </label>
													<p className="commonFontClass">
													{(booking?.total_service_count || booking?.service_count || "N/A")}
													</p>
												</div>
												<div>
													<label>Requests: </label>
												<div className={styles.iconContainer}>
													{serviceTypeIcons && serviceTypeIcons.length > 0 ? (
														serviceTypeIcons.map((iconItem, i) => (
															<span key={i} className={styles.requestItem}>
																<img
																	src={iconItem.icon}
																	alt={iconItem.title}
																	title={iconItem.title}
																	className={styles.iconStyle}
																/>
																<span className={styles.requestLabel}>{iconItem.title}</span>
															</span>
														))
													) : (
														<span className={styles.naText}>N/A</span>
													)}
												</div>
												</div>
												<div>
													<label>Therapist: </label>
													<p className="commonFontClass">
														{booking?.therapists && booking.therapists.trim().length > 0 ? booking.therapists : <span className={styles.naText}>N/A</span>}
													</p>
												</div>
											</div>
										</div>
									</div>
								);
							})
						) : (
							<div className={styles.noResults}>
								<Placeholder
                                    icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
                                    heading="No bookings found"
                                    para="Please check the name or phone number and try again."
                                />
							</div>
						)}
						{isLoading && page > 1 && (
							<div className={`${styles.loadingMore} greyFontClass`}>
								Loading...
							</div>
						)}
					</div>
				</div>
			</CustomModal>
		</>
	);
};

export default Search;
